import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import { c as classNames } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-slider.css';
const getClassNameList$1 = classNames.bind(styles);
const EGDSSliderInput = props => {
  const {
    absoluteMax,
    absoluteMin,
    disabled,
    formatValueForDisplay,
    gap,
    handleAfterChange,
    handleChange,
    id,
    isActive,
    label,
    lowerValue,
    max,
    min,
    name,
    onActive,
    step,
    type,
    upperValue,
    value,
    minDistance
  } = props;
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  const changeEventEmitter = (changeCb, event) => {
    // Fallbacks provided for linter
    const newMaxValue = max || absoluteMax;
    const newMinValue = min || absoluteMin;
    const innerValue = event && event.target && event.target.value;
    const parsed = innerValue && parseInt(innerValue, 10);
    switch (type) {
      case 'primary':
        if (newMaxValue - Math.min(newMaxValue - gap, parsed) >= minDistance) {
          changeCb(Math.min(newMaxValue - gap, parsed), newMaxValue, event);
        }
        break;
      case 'secondary':
        if (Math.max(newMinValue + gap, parsed) - newMinValue >= minDistance) {
          changeCb(newMinValue, Math.max(newMinValue + gap, parsed), event);
        }
        break;
      case 'single':
        changeCb(parsed, undefined, event);
        break;
      default:
        changeCb(parsed);
        break;
    }
  };
  const handleChangeEvt = event => {
    if (typeof onActive === 'function') {
      type && onActive(type);
    }
    if (typeof handleChange === 'function') {
      changeEventEmitter(handleChange, event);
    }
  };
  const handleAfterChangeEvt = event => {
    if (typeof handleAfterChange === 'function') {
      changeEventEmitter(handleAfterChange, event);
    }
  };
  const outputRef = React.createRef();
  let thumbPosition;
  const inputClasses = ['uitk-slider-input'];
  const outputClasses = ['uitk-slider-output'];
  const inputStyles = {};
  const sliderStyles = {};
  const componentId = `${id}-${type}`;
  // Fallbacks provided for linter
  const maxValue = max ?? absoluteMax;
  const minValue = min ?? absoluteMin;
  const newLowerValue = lowerValue ?? absoluteMin;
  const newUpperValue = upperValue ?? absoluteMax;
  const newValue = value ?? absoluteMin;
  if (isActive) {
    inputClasses.push('uitk-slider-input-active');
  }
  switch (type) {
    case 'primary':
      inputClasses.push('slider-primary');
      thumbPosition = 100 / ((maxValue - absoluteMin) / (newLowerValue - absoluteMin));
      if (!isFinite(thumbPosition)) {
        thumbPosition = 0;
      }
      inputStyles['--slider-primary'] = `${thumbPosition}%`;
      sliderStyles.width = `${100 / (absoluteMax - absoluteMin) * (newUpperValue - absoluteMin)}%`;
      sliderStyles['--slider-primary'] = `${thumbPosition}%`;
      break;
    case 'secondary':
      inputClasses.push('slider-secondary');
      thumbPosition = 100 - 100 / ((maxValue - minValue) / (maxValue - newUpperValue));
      if (!isFinite(thumbPosition)) {
        thumbPosition = 100;
      }
      inputStyles['--slider-secondary'] = `${thumbPosition}%`;
      sliderStyles['--slider-secondary'] = `${thumbPosition}%`;
      sliderStyles.width = `${100 - 100 / (absoluteMax - absoluteMin) * (newLowerValue - absoluteMin)}%`;
      break;
    default:
      inputClasses.push('slider-single', 'slider-primary');
      thumbPosition = 100 / ((maxValue - minValue) / (newValue - minValue));
      if (!isFinite(thumbPosition)) {
        thumbPosition = 0;
      }
      inputStyles['--slider-primary'] = `${thumbPosition}%`;
      sliderStyles['--slider-primary'] = `${thumbPosition}%`;
      sliderStyles.width = '100%';
      break;
  }
  const valueToDisplay = typeof formatValueForDisplay === 'function' ? formatValueForDisplay(newValue) : newValue;
  const baseInputAttrs = {
    'aria-label': label || name,
    'aria-valuetext': `${valueToDisplay}`,
    className: getClassNameList$1(inputClasses),
    disabled,
    id: componentId,
    max: maxValue,
    min: minValue,
    name,
    onChange: handleChangeEvt,
    onKeyUp: handleAfterChangeEvt,
    onMouseUp: handleAfterChangeEvt,
    onTouchEnd: handleAfterChangeEvt,
    step,
    style: inputStyles
  };
  return jsxs(Fragment, {
    children: [jsxs("div", {
      className: getClassNameList$1('uitk-slider'),
      style: sliderStyles,
      children: [mounted ? jsx(Fragment, {
        children: jsx("input", {
          type: "range",
          "aria-valuemax": maxValue,
          "aria-valuemin": minValue,
          "aria-valuenow": newValue,
          value: newValue,
          ...baseInputAttrs
        })
      }) :
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
      jsx("input", {
        type: "number",
        "aria-valuemax": maxValue,
        "aria-valuemin": minValue,
        "aria-valuenow": newValue,
        value: newValue,
        ...baseInputAttrs
      }), jsx("label", {
        className: "is-hidden",
        htmlFor: componentId,
        children: label || name
      })]
    }), jsx("span", {
      className: getClassNameList$1(outputClasses),
      ref: outputRef,
      "aria-hidden": "true",
      children: valueToDisplay
    })]
  });
};
EGDSSliderInput.displayName = 'EGDSSliderInput';
const getClassNameList = classNames.bind(styles);
const EGDSSlider = props => {
  const {
    disabled,
    formatSecondaryValueForDisplay,
    formatValueForDisplay,
    gap,
    handleAfterChange,
    handleChange,
    lowerValue,
    max,
    min,
    name,
    primaryLabel,
    secondaryLabel,
    step,
    ticks,
    type,
    upperValue,
    value,
    minDistance = 0
  } = props;
  const [active, setActive] = React.useState('');
  const [isFirefoxFallback, setIsFirefoxFallback] = React.useState(false);
  const [gapWidth, setGapWidth] = React.useState(0);
  const [mounted, setMounted] = React.useState(false);
  const sliderRef = React.createRef();
  const checkSliderWidth = () => {
    const newGap = gap || 0;
    const ref = sliderRef && sliderRef.current;
    const width = ref && ref.offsetWidth;
    if (width) {
      setGapWidth(Math.floor((max - min) * newGap / width));
    }
  };
  const checkFFBrowserFallback = () => {
    // eslint-disable-next-line no-negated-condition
    setIsFirefoxFallback(typeof window !== 'undefined' ? /Firefox\//.test(window.navigator.userAgent) : false);
  };
  React.useEffect(() => {
    setMounted(true);
    if (type === 'dual') {
      checkSliderWidth();
      checkFFBrowserFallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleChangeEvt = (primary, secondary, event) => {
    handleChange(primary, secondary, event);
  };
  const handleAfterChangeEvt = (primary, secondary, event) => {
    if (typeof handleAfterChange === 'function') {
      handleAfterChange(primary, secondary, event);
    }
  };
  const onActive = () => {
    if (typeof type !== 'undefined') setActive(type);
  };
  const getDisabled = () => !mounted || disabled;
  const isDual = isFirefoxFallback ? false : type === 'dual';
  const fieldClasses = ['uitk-slider-container', ...(ticks ? ['uitk-slider-container-has-ticks'] : [])];
  const formatSecondaryValueForDualSlider = typeof formatSecondaryValueForDisplay === 'function' ? formatSecondaryValueForDisplay : formatValueForDisplay;
  const cssStyles = {
    '--min': min,
    '--max': max,
    '--step': step
  };
  if (getDisabled()) {
    fieldClasses.push('uitk-slider-container-disabled');
  }
  if (step === undefined) {
    fieldClasses.push('uitk-slider-container-no-step');
  }
  return jsxs("div", {
    className: getClassNameList(fieldClasses),
    ref: sliderRef,
    style: cssStyles,
    children: [type === 'dual' && isFirefoxFallback && jsx("input", {
      type: "hidden",
      value: lowerValue,
      name: name
    }), jsx(EGDSSliderInput, {
      ...props,
      type: isDual ? 'primary' : 'single',
      name: name,
      max: isDual ? upperValue : max,
      value: isDual ? lowerValue : value || upperValue,
      absoluteMax: max,
      absoluteMin: min,
      gap: gapWidth,
      isActive: active === 'primary',
      onActive: onActive,
      disabled: getDisabled(),
      handleChange: handleChangeEvt,
      handleAfterChange: handleAfterChangeEvt,
      formatValueForDisplay: formatValueForDisplay,
      label: isDual ? primaryLabel : undefined,
      minDistance: minDistance
    }), isDual && jsx(EGDSSliderInput, {
      ...props,
      type: "secondary",
      name: name,
      min: lowerValue,
      value: upperValue,
      absoluteMax: max,
      absoluteMin: min,
      gap: gapWidth,
      isActive: active === 'secondary',
      onActive: onActive,
      disabled: getDisabled(),
      handleChange: handleChangeEvt,
      handleAfterChange: handleAfterChangeEvt,
      formatValueForDisplay: formatSecondaryValueForDualSlider,
      label: secondaryLabel,
      minDistance: minDistance
    })]
  });
};
EGDSSlider.displayName = 'EGDSSlider';
export { EGDSSlider as E, EGDSSliderInput as a };